import React, { useState, useEffect } from 'react';
import api from '../../config/axios';
import './SalaryManagement.css';
import Header from '../common/Header';
import { useNavigate } from 'react-router-dom';

interface Employee {
  _id: string;
  username: string;
  email: string;
  employmentType?: 'fulltime' | 'parttime';
  baseSalary: number;
  hourlyRate: number;
  commissionRate: number;
}

interface Salary {
  _id: string;
  staffId: Employee;
  month?: number;
  year?: number;
  periodIdentifier?: string;
  periodName?: string;
  startDate?: string;
  endDate?: string;
  employmentType?: 'fulltime' | 'parttime';
  baseSalary: number;
  hourlyRate: number;
  effectiveHourlyRate?: number;
  hourlyWage?: number;
  totalHours: number;
  totalShifts: number;
  totalOrders: number;
  totalRevenue: number;
  commission: number;
  bonus: number;
  deductions: number;
  netSalary: number;
  status: 'pending' | 'calculated' | 'paid';
  paidAt?: string;
  notes?: string;
  createdAt: string;
}

interface SalaryManagementProps {
  user: any;
}

const SalaryManagement: React.FC<SalaryManagementProps> = ({ user }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [salaries, setSalaries] = useState<Salary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showCalculateForm, setShowCalculateForm] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<Salary | null>(null);
  const [payNotes, setPayNotes] = useState('');
  const [showSalaryUpdateForm, setShowSalaryUpdateForm] = useState(false);
  const [selectedEmployeeForUpdate, setSelectedEmployeeForUpdate] = useState<Employee | null>(null);
  const [salaryUpdateData, setSalaryUpdateData] = useState({
    baseSalary: '',
    hourlyRate: '',
    commissionRate: ''
  });
  const [showEditSalaryForm, setShowEditSalaryForm] = useState(false);
  const [selectedSalaryForEdit, setSelectedSalaryForEdit] = useState<Salary | null>(null);
  const [editSalaryData, setEditSalaryData] = useState({
    baseSalary: '',
    hourlyRate: '',
    bonus: '',
    deductions: '',
    notes: ''
  });
  const navigate = useNavigate();

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/api/employees');
      setEmployees(res.data.data);
    } catch (err) {
      setError('Không thể tải danh sách nhân viên');
    }
  };

  const fetchSalaries = async () => {
    try {
      const res = await api.get('/api/salaries');
      setSalaries(res.data.data);
    } catch (err) {
      setError('Không thể tải danh sách lương');
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchSalaries();
  }, []);

  const handleCalculateSalary = async () => {
    if (!selectedEmployee) {
      setError('Vui lòng chọn nhân viên');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/salaries/calculate', {
        staffId: selectedEmployee,
        month: selectedMonth,
        year: selectedYear
      });

      setSuccess('Tính lương thành công!');
      setShowCalculateForm(false);
      fetchSalaries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tính lương');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedSalary) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/api/salaries/${selectedSalary._id}/pay`, {
        notes: payNotes
      });

      setSuccess('Đã đánh dấu thanh toán lương!');
      setShowPayForm(false);
      setSelectedSalary(null);
      setPayNotes('');
      fetchSalaries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi thanh toán lương');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEmployeeSalary = async () => {
    if (!selectedEmployeeForUpdate) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put(`/api/salaries/employee/${selectedEmployeeForUpdate._id}`, {
        baseSalary: Number(salaryUpdateData.baseSalary),
        hourlyRate: Number(salaryUpdateData.hourlyRate),
        commissionRate: Number(salaryUpdateData.commissionRate)
      });

      setSuccess('Cập nhật thông tin lương thành công!');
      setShowSalaryUpdateForm(false);
      setSelectedEmployeeForUpdate(null);
      setSalaryUpdateData({ baseSalary: '', hourlyRate: '', commissionRate: '' });
      fetchEmployees();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi cập nhật thông tin lương');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSalary = async () => {
    if (!selectedSalaryForEdit) return;

    if (!editSalaryData.baseSalary || !editSalaryData.hourlyRate) {
      setError('Vui lòng nhập đầy đủ thông tin lương cơ bản và lương theo giờ');
      return;
    }

    const baseSalary = Number(editSalaryData.baseSalary);
    const hourlyRate = Number(editSalaryData.hourlyRate);
    const bonus = Number(editSalaryData.bonus) || 0;
    const deductions = Number(editSalaryData.deductions) || 0;

    if (isNaN(baseSalary) || baseSalary < 0) {
      setError('Lương cơ bản không hợp lệ');
      return;
    }

    if (isNaN(hourlyRate) || hourlyRate < 0) {
      setError('Lương theo giờ không hợp lệ');
      return;
    }

    if (isNaN(bonus) || bonus < 0) {
      setError('Thưởng không hợp lệ');
      return;
    }

    if (isNaN(deductions) || deductions < 0) {
      setError('Khấu trừ không hợp lệ');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.put(`/api/salaries/${selectedSalaryForEdit._id}`, {
        baseSalary,
        hourlyRate,
        bonus,
        deductions,
        notes: editSalaryData.notes
      });

      setSuccess('Cập nhật bảng lương thành công!');
      setShowEditSalaryForm(false);
      setSelectedSalaryForEdit(null);
      setEditSalaryData({ baseSalary: '', hourlyRate: '', bonus: '', deductions: '', notes: '' });
      fetchSalaries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi cập nhật bảng lương');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSalary = async (salaryId: string) => {
    if (!window.confirm('Bạn chắc chắn muốn xóa bảng lương này?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.delete(`/api/salaries/${salaryId}`);

      setSuccess('Xóa bảng lương thành công!');
      fetchSalaries();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi xóa bảng lương');
    } finally {
      setLoading(false);
    }
  };

  const openSalaryUpdateForm = (employee: Employee) => {
    setSelectedEmployeeForUpdate(employee);
    setSalaryUpdateData({
      baseSalary: employee.baseSalary?.toString() || '',
      hourlyRate: employee.hourlyRate?.toString() || '',
      commissionRate: employee.commissionRate?.toString() || ''
    });
    setShowSalaryUpdateForm(true);
  };

  const openEditSalaryForm = (salary: Salary) => {
    setSelectedSalaryForEdit(salary);
    setEditSalaryData({
      baseSalary: salary.baseSalary?.toString() || '0',
      hourlyRate: salary.hourlyRate?.toString() || '0',
      bonus: salary.bonus?.toString() || '0',
      deductions: salary.deductions?.toString() || '0',
      notes: salary.notes || ''
    });
    setShowEditSalaryForm(true);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Chờ tính';
      case 'calculated': return 'Đã tính';
      case 'paid': return 'Đã thanh toán';
      default: return status;
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'calculated': return 'status-calculated';
      case 'paid': return 'status-paid';
      default: return '';
    }
  };

  const formatCurrency = (amount: number) => {
    return Math.round(amount).toLocaleString('vi-VN') + ' ₫';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/';
  };

  const employeesWithoutSalary = employees.filter(emp => !emp.baseSalary && !emp.hourlyRate);

  return (
    <>
      <Header user={user} onLogout={handleLogout}>
        <span className="user-role">Quản lý</span>
      </Header>
      <div className="salary-container">
        <div className="salary-header">
          <button
            className="salary-back-btn"
            onClick={() => navigate('/')}
            title="Quay lại Dashboard"
          >
            ←
          </button>
          <h2 className="salary-title">QUẢN LÝ LƯƠNG NHÂN VIÊN</h2>
        </div>

        {error && <div className="salary-error">{error}</div>}
        {success && <div className="salary-success">{success}</div>}
        {loading && <div className="salary-loading">Đang xử lý...</div>}

        {employeesWithoutSalary.length > 0 && (
          <div className="salary-warning">
            <h3>⚠️ Cảnh báo: Nhân viên chưa có thông tin lương</h3>
            <p>Có {employeesWithoutSalary.length} nhân viên chưa được thiết lập thông tin lương. Vui lòng cập nhật trước khi tính lương.</p>
            <div className="salary-warning-list">
              {employeesWithoutSalary.map(emp => (
                <div key={emp._id} className="salary-warning-item">
                  <span>{emp.username}</span>
                  <button
                    className="salary-update-btn"
                    onClick={() => openSalaryUpdateForm(emp)}
                  >
                    Cập nhật lương
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="salary-actions">
          <button 
            className="salary-calculate-btn"
            onClick={() => setShowCalculateForm(true)}
            disabled={employeesWithoutSalary.length > 0}
          >
            + Tính lương mới
          </button>
        </div>

        <div className="salary-table-container">
          <h3 className="salary-section-title">Danh sách bảng lương</h3>
          {salaries.length === 0 ? (
            <div className="salary-empty">Chưa có bảng lương nào.</div>
          ) : (
            <table className="salary-table">
              <thead>
                <tr>
                  <th>Nhân viên</th>
                  <th>Tháng/Năm</th>
                  <th>Hình thức</th>
                  <th>Giờ làm</th>
                  <th>Tiền công theo giờ</th>
                  <th>Đơn hàng</th>
                  <th>Doanh thu</th>
                  <th>Hoa hồng</th>
                  <th>Lương thực nhận</th>
                  <th>Trạng thái</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {salaries.map(salary => (
                  <tr key={salary._id}>
                    <td>{salary.staffId.username}</td>
                    <td>
                      {salary.periodName || 
                       (salary.month && salary.year 
                         ? `${salary.month}/${salary.year}`
                         : `${salary.startDate} - ${salary.endDate}`
                       )}
                    </td>
                    <td>{salary.employmentType === 'fulltime' ? 'Fulltime' : 'Parttime'}</td>
                    <td>{salary.totalHours}h</td>
                    <td title={`${formatCurrency(salary.effectiveHourlyRate ?? 0)}/h`}>{formatCurrency(salary.hourlyWage ?? 0)}</td>
                    <td>{salary.totalOrders}</td>
                    <td>{formatCurrency(salary.totalRevenue)}</td>
                    <td>{formatCurrency(salary.commission)}</td>
                    <td className="salary-net">{formatCurrency(salary.netSalary)}</td>
                    <td>
                      <span className={`salary-status ${getStatusClass(salary.status)}`}>
                        {getStatusText(salary.status)}
                      </span>
                    </td>
                    <td className="salary-actions-cell">
                      {salary.status === 'calculated' && (
                        <>
                          <button
                            className="salary-pay-btn"
                            onClick={() => {
                              setSelectedSalary(salary);
                              setShowPayForm(true);
                            }}
                          >
                            Thanh toán
                          </button>
                          <button
                            className="salary-edit-btn"
                            onClick={() => openEditSalaryForm(salary)}
                          >
                            Sửa
                          </button>
                        </>
                      )}
                      {salary.status === 'paid' && (
                        <>
                          <span className="salary-paid-date">
                            {formatDate(salary.paidAt!)}
                          </span>
                          <button
                            className="salary-edit-btn"
                            onClick={() => openEditSalaryForm(salary)}
                          >
                            Sửa
                          </button>
                        </>
                      )}
                      {salary.status !== 'paid' && (
                        <button
                          className="salary-delete-btn"
                          onClick={() => handleDeleteSalary(salary._id)}
                        >
                          Xóa
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showCalculateForm && (
          <div className="salary-modal">
            <div className="salary-modal-content">
              <h3>Tính lương mới</h3>
              <div className="salary-form-group">
                <label>Chọn nhân viên:</label>
                <select
                  value={selectedEmployee}
                  onChange={(e) => setSelectedEmployee(e.target.value)}
                  className="salary-select"
                >
                  <option value="">-- Chọn nhân viên --</option>
                  {employees.filter(emp => emp.baseSalary || emp.hourlyRate).map(emp => (
                    <option key={emp._id} value={emp._id}>
                      {emp.username} - {formatCurrency(emp.baseSalary || 0)} + {formatCurrency(emp.hourlyRate || 0)}/h
                    </option>
                  ))}
                </select>
              </div>
              <div className="salary-form-group">
                <label>Tháng:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="salary-select"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>
                      Tháng {month}
                    </option>
                  ))}
                </select>
              </div>
              <div className="salary-form-group">
                <label>Năm:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="salary-select"
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="salary-modal-actions">
                <button
                  onClick={handleCalculateSalary}
                  disabled={loading || !selectedEmployee}
                  className="salary-confirm-btn"
                >
                  {loading ? 'Đang tính...' : 'Tính lương'}
                </button>
                <button
                  onClick={() => setShowCalculateForm(false)}
                  className="salary-cancel-btn"
                >
                  Hủy
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}

        {showPayForm && selectedSalary && (
          <div className="salary-modal">
            <div className="salary-modal-content">
              <h3>Thanh toán lương</h3>
              <div className="salary-pay-info">
                <div className="salary-pay-info-item">
                  <span className="salary-pay-info-label">Nhân viên:</span>
                  <span className="salary-pay-info-value">{selectedSalary.staffId.username}</span>
                </div>
                <div className="salary-pay-info-item">
                  <span className="salary-pay-info-label">Khoảng thời gian:</span>
                  <span className="salary-pay-info-value">
                    {selectedSalary.periodName || 
                     (selectedSalary.month && selectedSalary.year 
                       ? `${selectedSalary.month}/${selectedSalary.year}`
                       : `${selectedSalary.startDate} - ${selectedSalary.endDate}`
                     )}
                  </span>
                </div>
                <div className="salary-pay-info-item">
                  <span className="salary-pay-info-label">Lương thực nhận:</span>
                  <span className="salary-pay-info-value">{formatCurrency(selectedSalary.netSalary)}</span>
                </div>
              </div>
              <div className="salary-form-group">
                <label>Ghi chú:</label>
                <textarea
                  value={payNotes}
                  onChange={(e) => setPayNotes(e.target.value)}
                  className="salary-textarea"
                  placeholder="Ghi chú về việc thanh toán..."
                />
              </div>
              <div className="salary-modal-actions">
                <button
                  onClick={handleMarkPaid}
                  disabled={loading}
                  className="salary-confirm-btn"
                >
                  {loading ? 'Đang xử lý...' : 'Xác nhận thanh toán'}
                </button>
                <button
                  onClick={() => {
                    setShowPayForm(false);
                    setSelectedSalary(null);
                    setPayNotes('');
                  }}
                  className="salary-cancel-btn"
                >
                  Hủy
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}

        {showSalaryUpdateForm && selectedEmployeeForUpdate && (
          <div className="salary-modal">
            <div className="salary-modal-content">
              <h3>Cập nhật thông tin lương</h3>
              <div className="salary-update-info">
                <p><strong>Nhân viên:</strong> {selectedEmployeeForUpdate.username}</p>
                <p><strong>Email:</strong> {selectedEmployeeForUpdate.email}</p>
              </div>
              <div className="salary-form-group">
                <label>Lương cơ bản (₫):</label>
                <input
                  type="number"
                  value={salaryUpdateData.baseSalary}
                  onChange={(e) => setSalaryUpdateData(prev => ({ ...prev, baseSalary: e.target.value }))}
                  className="salary-input"
                  min="0"
                  placeholder="Nhập lương cơ bản"
                />
              </div>
              <div className="salary-form-group">
                <label>Lương theo giờ (₫/h):</label>
                <input
                  type="number"
                  value={salaryUpdateData.hourlyRate}
                  onChange={(e) => setSalaryUpdateData(prev => ({ ...prev, hourlyRate: e.target.value }))}
                  className="salary-input"
                  min="0"
                  placeholder="Nhập lương theo giờ"
                />
              </div>
              <div className="salary-form-group">
                <label>Tỷ lệ hoa hồng (%):</label>
                <input
                  type="number"
                  value={salaryUpdateData.commissionRate}
                  onChange={(e) => setSalaryUpdateData(prev => ({ ...prev, commissionRate: e.target.value }))}
                  className="salary-input"
                  min="0"
                  max="100"
                  step="0.1"
                  placeholder="Nhập tỷ lệ hoa hồng"
                />
              </div>
              <div className="salary-modal-actions">
                <button
                  onClick={handleUpdateEmployeeSalary}
                  disabled={loading}
                  className="salary-confirm-btn"
                >
                  {loading ? 'Đang cập nhật...' : 'Cập nhật'}
                </button>
                <button
                  onClick={() => {
                    setShowSalaryUpdateForm(false);
                    setSelectedEmployeeForUpdate(null);
                    setSalaryUpdateData({ baseSalary: '', hourlyRate: '', commissionRate: '' });
                  }}
                  className="salary-cancel-btn"
                >
                  Hủy
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}

        {showEditSalaryForm && selectedSalaryForEdit && (
          <div className="salary-modal">
            <div className="salary-modal-content">
              <h3>Sửa bảng lương</h3>
              <div className="salary-update-info">
                <p><strong>Nhân viên:</strong> {selectedSalaryForEdit.staffId.username}</p>
                <p><strong>Tháng/Năm:</strong> {selectedSalaryForEdit.month}/{selectedSalaryForEdit.year}</p>
                <p><strong>Trạng thái:</strong> {getStatusText(selectedSalaryForEdit.status)}</p>
              </div>
              <div className="salary-form-group">
                <label>Lương cơ bản (₫):</label>
                <input
                  type="number"
                  value={editSalaryData.baseSalary}
                  onChange={(e) => setEditSalaryData(prev => ({ ...prev, baseSalary: e.target.value }))}
                  className="salary-input"
                  min="0"
                />
              </div>
              <div className="salary-form-group">
                <label>Lương theo giờ (₫/h):</label>
                <input
                  type="number"
                  value={editSalaryData.hourlyRate}
                  onChange={(e) => setEditSalaryData(prev => ({ ...prev, hourlyRate: e.target.value }))}
                  className="salary-input"
                  min="0"
                />
              </div>
              <div className="salary-form-group">
                <label>Thưởng (₫):</label>
                <input
                  type="number"
                  value={editSalaryData.bonus}
                  onChange={(e) => setEditSalaryData(prev => ({ ...prev, bonus: e.target.value }))}
                  className="salary-input"
                  min="0"
                />
              </div>
              <div className="salary-form-group">
                <label>Khấu trừ (₫):</label>
                <input
                  type="number"
                  value={editSalaryData.deductions}
                  onChange={(e) => setEditSalaryData(prev => ({ ...prev, deductions: e.target.value }))}
                  className="salary-input"
                  min="0"
                />
              </div>
              <div className="salary-form-group">
                <label>Ghi chú:</label>
                <textarea
                  value={editSalaryData.notes}
                  onChange={(e) => setEditSalaryData(prev => ({ ...prev, notes: e.target.value }))}
                  className="salary-textarea"
                  placeholder="Ghi chú..."
                />
              </div>
              <div className="salary-modal-actions">
                <button
                  onClick={handleEditSalary}
                  disabled={loading}
                  className="salary-confirm-btn"
                >
                  {loading ? 'Đang cập nhật...' : 'Cập nhật'}
                </button>
                <button
                  onClick={() => {
                    setShowEditSalaryForm(false);
                    setSelectedSalaryForEdit(null);
                    setEditSalaryData({ baseSalary: '', hourlyRate: '', bonus: '', deductions: '', notes: '' });
                  }}
                  className="salary-cancel-btn"
                >
                  Hủy
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SalaryManagement; 